import { useState, useEffect, useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { localizeApiDetail } from '../../i18n/apiError'
import { ChevronRight, Search } from 'lucide-react'
import { leadsAPI } from './api'
import type {
  HendelseMerkeFacet,
  HendelseScope,
  HendelseTabRow,
  HendelseType,
  HendelsePerOrg,
  HendelseSummary,
} from './api'
import Pagination from './Pagination'
import ExportButtons from '../../components/ExportButtons'
import { useKanEksportere } from './eksportTilgang'
import { formatNorwegianDate } from '../../utils/dateUtils'

interface Props {
  page: number
  onPageChange: (page: number) => void
}

const PAGE_SIZE = 50

const SCOPES = [
  { value: 'mine_org', labelKey: 'hendelser.scopes.mineOrg' },
  { value: 'mine_leads', labelKey: 'hendelser.scopes.mineLeads' },
  { value: 'begge', labelKey: 'hendelser.scopes.begge' },
] as const satisfies readonly { value: HendelseScope; labelKey: string }[]

const TYPE_LABELS = {
  eie: 'hendelser.types.eie',
  leasing: 'hendelser.types.leasing',
  solgt: 'hendelser.types.solgt',
  kjopt: 'hendelser.types.kjopt',
} as const satisfies Record<HendelseType, string>

const TYPE_STYLES: Record<HendelseType, string> = {
  eie: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300',
  leasing: 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300',
  solgt: 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300',
  kjopt: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300',
}

/** January 1st of the current year — the default window is year-to-date (#473). */
const startOfYear = () => `${new Date().getFullYear()}-01-01`

const today = () => new Date().toISOString().slice(0, 10)

/**
 * Hendelser: the same four event sources as Hendelsessenter, but as a working
 * list — every row names the actual car, and the scope can be the user's own
 * organisations, their bookmarked leads, or both.
 *
 * Two date axes, because they answer different questions: the window bounds
 * when the event happened, the registration filter bounds how old the car is.
 */
export default function HendelserTab({ page, onPageChange }: Props) {
  const { t } = useTranslation('leads')
  const kanEksportere = useKanEksportere()
  const navigate = useNavigate()
  const location = useLocation()

  // Draft filters — applied on Søk, so a heavy query is not re-run per keystroke
  const [draftFrom, setDraftFrom] = useState(startOfYear())
  const [draftTo, setDraftTo] = useState(today())
  const [draftScope, setDraftScope] = useState<HendelseScope>('mine_org')
  const [draftOrg, setDraftOrg] = useState('')
  const [draftType, setDraftType] = useState<HendelseType | ''>('')
  const [draftMerker, setDraftMerker] = useState<Set<string>>(new Set())
  const [draftRegFrom, setDraftRegFrom] = useState('')
  const [draftRegTo, setDraftRegTo] = useState('')

  const [applied, setApplied] = useState({
    from: startOfYear(), to: today(),
    scope: 'mine_org' as HendelseScope,
    org: '', type: '' as HendelseType | '',
    merker: [] as string[], regFrom: '', regTo: '',
  })

  const [merkeFacet, setMerkeFacet] = useState<HendelseMerkeFacet[]>([])
  const [merkeLoading, setMerkeLoading] = useState(true)

  const [rows, setRows] = useState<HendelseTabRow[]>([])
  const [perOrg, setPerOrg] = useState<HendelsePerOrg[]>([])
  const [summary, setSummary] = useState<HendelseSummary | null>(null)
  const [total, setTotal] = useState(0)
  const [scopeCapped, setScopeCapped] = useState(false)
  const [resultCapped, setResultCapped] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // The facet follows the applied window and scope, not the drafts — it has to
  // describe the data being looked at, not a query nobody has run.
  useEffect(() => {
    let cancelled = false
    setMerkeLoading(true)
    leadsAPI.getHendelserMerker(applied.from, applied.to, applied.scope, applied.type || undefined)
      .then(res => { if (!cancelled) setMerkeFacet(res.data.items) })
      .catch(() => { if (!cancelled) setMerkeFacet([]) })
      .finally(() => { if (!cancelled) setMerkeLoading(false) })
    return () => { cancelled = true }
  }, [applied.from, applied.to, applied.scope, applied.type])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    leadsAPI.getHendelser({
      from: applied.from,
      to: applied.to,
      scope: applied.scope,
      org: applied.org || undefined,
      type: applied.type || undefined,
      merke: applied.merker.length ? applied.merker : undefined,
      reg_from: applied.regFrom || undefined,
      reg_to: applied.regTo || undefined,
      page,
      page_size: PAGE_SIZE,
    })
      .then(res => {
        if (cancelled) return
        setRows(res.data.rows)
        setPerOrg(res.data.per_org)
        setSummary(res.data.summary)
        setTotal(res.data.pagination.total)
        setScopeCapped(res.data.scope_is_capped)
        setResultCapped(res.data.result_is_capped)
      })
      .catch(err => { if (!cancelled) setError(localizeApiDetail(err.response?.data?.detail) || err.message) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [applied, page])

  const windowInvalid = draftFrom !== '' && draftTo !== '' && draftFrom > draftTo
  const regRangeInvalid = draftRegFrom !== '' && draftRegTo !== '' && draftRegFrom > draftRegTo

  function handleSearch() {
    setApplied({
      from: draftFrom, to: draftTo, scope: draftScope,
      org: draftOrg.trim(), type: draftType,
      merker: [...draftMerker], regFrom: draftRegFrom, regTo: draftRegTo,
    })
    onPageChange(1)
  }

  function toggleMerke(merke: string) {
    setDraftMerker(prev => {
      const next = new Set(prev)
      if (next.has(merke)) next.delete(merke)
      else next.add(merke)
      return next
    })
  }

  const exportColumns = [
    { key: 'registrert', label: t('common.dato') },
    { key: 'type', label: t('common.type') },
    { key: 'kjennemerke', label: t('common.kjennemerke') },
    { key: 'understellsnummer', label: t('hendelser.col.understellsnummer') },
    { key: 'merke', label: t('common.merke') },
    { key: 'modell', label: t('common.modell') },
    { key: 'forstegang_reg', label: t('hendelser.col.firstRegistered') },
    { key: 'org_nr', label: t('common.orgNr') },
    { key: 'org_navn', label: t('hendelser.col.orgNavn') },
    { key: 'postnummer', label: t('hendelser.col.postnummer') },
    { key: 'poststed', label: t('common.poststed') },
    { key: 'source', label: t('common.kilde') },
  ]

  const exportMetadata = useMemo(() => {
    const scope = SCOPES.find(s => s.value === applied.scope)
    return {
      title: t('hendelser.exportTitle'),
      dateRange: `${applied.from} - ${applied.to}`,
      filters: [
        t('hendelser.filter.omfang', { v: scope ? t(scope.labelKey) : undefined }),
        applied.type ? t('hendelser.filter.type', { v: t(TYPE_LABELS[applied.type]) }) : t('hendelser.filter.typeAll'),
        applied.merker.length ? t('hendelser.filter.merke', { v: applied.merker.join(', ') }) : t('hendelser.filter.merkeAll'),
        applied.org ? t('hendelser.filter.org', { v: applied.org }) : t('hendelser.filter.orgAll'),
        applied.regFrom || applied.regTo
          ? t('hendelser.filter.firstReg', { from: applied.regFrom || '...', to: applied.regTo || '...' })
          : t('hendelser.filter.firstRegAll'),
      ],
    }
  }, [applied, t])

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="card p-4 space-y-3">
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">{t('hendelser.omfang')}</label>
            <select
              value={draftScope}
              onChange={e => setDraftScope(e.target.value as HendelseScope)}
              className="input-field text-sm min-w-[180px]"
            >
              {SCOPES.map(s => <option key={s.value} value={s.value}>{t(s.labelKey)}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">{t('hendelser.hendelseFra')}</label>
            <input type="date" value={draftFrom} onChange={e => setDraftFrom(e.target.value)} className="input-field text-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">{t('hendelser.hendelseTil')}</label>
            <input type="date" value={draftTo} onChange={e => setDraftTo(e.target.value)} className="input-field text-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">{t('common.type')}</label>
            <select
              value={draftType}
              onChange={e => setDraftType(e.target.value as HendelseType | '')}
              className="input-field text-sm"
            >
              <option value="">{t('common.all')}</option>
              {(Object.keys(TYPE_LABELS) as HendelseType[]).map(ty => (
                <option key={ty} value={ty}>{t(TYPE_LABELS[ty])}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">{t('hendelser.organisasjon')}</label>
            <input
              type="text"
              value={draftOrg}
              onChange={e => setDraftOrg(e.target.value)}
              placeholder={t('hendelser.orgPlaceholder')}
              className="input-field text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">{t('hendelser.regFrom')}</label>
            <input type="month" value={draftRegFrom} onChange={e => setDraftRegFrom(e.target.value)} className="input-field text-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">{t('hendelser.regTo')}</label>
            <input type="month" value={draftRegTo} onChange={e => setDraftRegTo(e.target.value)} className="input-field text-sm" />
          </div>
          <button
            onClick={handleSearch}
            disabled={windowInvalid || regRangeInvalid}
            className="btn-primary flex items-center gap-2 disabled:opacity-50"
          >
            <Search className="w-4 h-4" />
            {t('common.search')}
          </button>
          {windowInvalid && (
            <span className="text-xs text-red-600 dark:text-red-400">{t('hendelser.dateRangeInvalid')}</span>
          )}
          {regRangeInvalid && (
            <span className="text-xs text-red-600 dark:text-red-400">{t('common.rangeMonthInvalid')}</span>
          )}
        </div>

        {/* Merke facet */}
        <div>
          <div className="flex items-center gap-3 mb-2">
            <label className="text-xs font-medium text-gray-600 dark:text-gray-400">
              {t('hendelser.merker')} {draftMerker.size > 0 && (
                <span className="text-primary-600 dark:text-primary-400">{t('hendelser.merkerSelected', { n: draftMerker.size })}</span>
              )}
            </label>
            {draftMerker.size > 0 && (
              <button onClick={() => setDraftMerker(new Set())} className="text-xs text-gray-500 hover:underline">
                {t('common.reset')}
              </button>
            )}
          </div>
          {merkeLoading ? (
            <p className="text-sm text-gray-500 py-2">{t('common.loadingMerker')}</p>
          ) : merkeFacet.length === 0 ? (
            <p className="text-sm text-gray-500 py-2">{t('hendelser.noMerker')}</p>
          ) : (
            <div className="max-h-40 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-sm p-2 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-1">
              {merkeFacet.map(m => (
                <label key={m.merke} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 rounded-sm px-1">
                  <input
                    type="checkbox"
                    checked={draftMerker.has(m.merke)}
                    onChange={() => toggleMerke(m.merke)}
                    className="rounded-sm"
                  />
                  <span className="truncate" title={m.merke}>{m.merke}</span>
                  <span className="text-xs text-gray-500 ml-auto">{m.car_count.toLocaleString('nb-NO')}</span>
                </label>
              ))}
            </div>
          )}
        </div>
      </div>

      {error && <div className="card p-6 text-red-600 dark:text-red-400">{error}</div>}

      {scopeCapped && !error && (
        <div className="card p-3 text-sm text-amber-800 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20">
          {t('hendelser.scopeCapped')}
        </div>
      )}
      {resultCapped && !error && (
        <div className="card p-3 text-sm text-amber-800 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20">
          {t('hendelser.resultCapped')}
        </div>
      )}

      {!error && summary && !loading && (
        <div className="card p-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 text-sm">
            <div><div className="text-xs text-gray-500">{t('hendelser.summary.hendelser')}</div><div className="text-lg font-bold">{summary.total_cars.toLocaleString('nb-NO')}</div></div>
            <div><div className="text-xs text-gray-500">{t('hendelser.summary.organisasjoner')}</div><div className="text-lg font-bold">{summary.total_orgs.toLocaleString('nb-NO')}</div></div>
            <div><div className="text-xs text-gray-500">{t('hendelser.types.eie')}</div><div className="text-lg font-bold">{summary.eie_count.toLocaleString('nb-NO')}</div></div>
            <div><div className="text-xs text-gray-500">{t('hendelser.types.leasing')}</div><div className="text-lg font-bold">{summary.leasing_count.toLocaleString('nb-NO')}</div></div>
            <div><div className="text-xs text-gray-500">{t('hendelser.types.solgt')}</div><div className="text-lg font-bold">{summary.solgt_count.toLocaleString('nb-NO')}</div></div>
            <div><div className="text-xs text-gray-500">{t('hendelser.types.kjopt')}</div><div className="text-lg font-bold">{summary.kjopt_count.toLocaleString('nb-NO')}</div></div>
          </div>
        </div>
      )}

      <div className="card p-6">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-gray-500 dark:text-gray-400">
            <svg className="animate-spin h-6 w-6 mr-3" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            {t('hendelser.loading')}
          </div>
        ) : rows.length === 0 ? (
          <p className="text-center text-gray-500 dark:text-gray-400 py-8">
            {t('hendelser.empty')}
            {perOrg.length === 0 && ` ${t('hendelser.emptyHint')}`}
          </p>
        ) : (
          <>
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('hendelser.count', { n: total.toLocaleString('nb-NO') })}
              </p>
              {kanEksportere && <ExportButtons data={rows} columns={exportColumns} metadata={exportMetadata} />}
            </div>
            <div className="overflow-x-auto">
              <table className="data-table data-table-compact w-full">
                <thead>
                  <tr>
                    <th className="text-left whitespace-nowrap">{t('common.dato')}</th>
                    <th className="text-left whitespace-nowrap">{t('common.type')}</th>
                    <th className="text-left whitespace-nowrap">{t('common.kjennemerke')}</th>
                    <th className="text-left whitespace-nowrap">{t('hendelser.col.understellsnummer')}</th>
                    <th className="text-left whitespace-nowrap">{t('common.merke')}</th>
                    <th className="text-left whitespace-nowrap">{t('common.modell')}</th>
                    <th className="text-left whitespace-nowrap">{t('common.firstReg')}</th>
                    <th className="text-left whitespace-nowrap">{t('common.orgNr')}</th>
                    <th className="text-left">{t('hendelser.col.orgNavn')}</th>
                    <th className="text-left whitespace-nowrap">{t('common.postnr')}</th>
                    <th className="text-left whitespace-nowrap">{t('common.poststed')}</th>
                    <th className="text-center whitespace-nowrap">{t('common.details')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, idx) => (
                    <tr
                      key={`${row.type}-${row.kjennemerke}-${row.registrert}-${idx}`}
                      className={`${idx % 2 === 0 ? 'bg-gray-50 dark:bg-gray-800/30' : ''} hover:bg-gray-100 dark:hover:bg-dark-border transition-colors`}
                    >
                      <td className="text-sm whitespace-nowrap">{formatNorwegianDate(row.registrert)}</td>
                      <td>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-sm text-xs ${TYPE_STYLES[row.type]}`}>
                          {t(TYPE_LABELS[row.type])}
                        </span>
                      </td>
                      <td className="font-mono text-xs font-bold">{row.kjennemerke ?? '—'}</td>
                      <td className="font-mono text-xs">{row.understellsnummer ?? '—'}</td>
                      <td className="text-sm">{row.merke ?? '—'}</td>
                      <td className="text-sm">{row.modell ?? '—'}</td>
                      <td className="text-sm whitespace-nowrap">{row.forstegang_reg ? formatNorwegianDate(row.forstegang_reg) : '—'}</td>
                      <td className="font-mono text-xs text-primary-600 dark:text-primary-400">{row.org_nr}</td>
                      <td className="text-sm">{row.org_navn ?? '—'}</td>
                      <td className="font-mono text-xs">{row.postnummer ?? '—'}</td>
                      <td className="text-sm">{row.poststed ?? '—'}</td>
                      <td className="text-center">
                        <button
                          onClick={() => navigate(`/leads/bedrift/${row.org_nr}`, {
                            state: { from: location.pathname + location.search },
                          })}
                          className="inline-flex items-center gap-1 text-primary-600 dark:text-primary-400 hover:underline"
                        >
                          <span className="text-xs font-medium">{t('common.see')}</span>
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Pagination
              page={page}
              onPageChange={onPageChange}
              total={total}
              totalIsCapped={resultCapped}
              hasMore={page * PAGE_SIZE < total}
              pageSize={PAGE_SIZE}
            />
          </>
        )}
      </div>
    </div>
  )
}
